import { AddCircleOutline, ExpandMore, FolderOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  IconButton,
  List,
  Skeleton,
  styled,
  Typography,
} from '@mui/material';
import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useClassicNavigate } from '../../hooks/useClassicNavigate';
import { getFolders } from '../../redux/actions/folderActions';
import { selectAppStatus, setFolderListOpen } from '../../redux/reducers/appStatusReducer';
import { selectFolders, setCurrentFolder, setIsLoading } from '../../redux/reducers/folderReducer';
import { selectNavDrawer } from '../../redux/reducers/navDrawerReducer';
import { AppDispatch } from '../../redux/store';
import { AppEnum } from '../../Types/enums';
import { Folder } from '../../Types/folder';
import FolderListItem from '../Folder/FolderListItem';
import { AnalyticsEvent, useAnalytics } from '../Providers/AnalyticsProvider';
import { useAuth } from '../Providers/AuthProvider';

const FilterControlsContainer = styled('div')({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

const StyledList = styled(List)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'start',
  width: '100%',
  justifyContent: 'flex-start',
  gap: '5px',
  padding: '0px',
});

const LoadMoreButton = styled(Button)({
  margin: '4px 0px 8px 0px',
  color: 'black',
  fontWeight: 'bold',
});

interface FolderListProps {
  onFolderCardClick: (folder: Folder) => void;
  folderListHeight: string;
}

const FolderList = ({ onFolderCardClick, folderListHeight }: FolderListProps) => {
  const analytics = useAnalytics();
  const navigate = useClassicNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isDealGPTAdmin } = useAuth();
  const { pinnedFolders, unpinnedFolders, totalCount, isLoading } = useSelector(selectFolders);
  const { selectedApp, folderListOpen, isSwitchingApps } = useSelector(selectAppStatus);
  const { searchQuery } = useSelector(selectNavDrawer);

  const isIntellio = selectedApp === AppEnum.DEAL_GPT;

  const shouldShowLoadMoreButton =
    unpinnedFolders.length + pinnedFolders.length > 0 &&
    unpinnedFolders.length + pinnedFolders.length < totalCount;

  const onNewFolderClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      analytics.event(AnalyticsEvent.WM_NIGEL_FOLDER_CREATE);
      navigate('/folder/create');
    },
    [navigate, analytics]
  );

  const loadMoreFolders = () => {
    analytics.event(AnalyticsEvent.WM_NIGEL_LOAD_MORE_FOLDERS, undefined, {
      numFoldersOld: unpinnedFolders.length,
    });
    dispatch(setIsLoading(true));
    dispatch(
      getFolders({
        pageAction: 'increment',
        foldersWithProjects: isIntellio,
        searchQuery,
      })
    );
  };

  const hasFolders = unpinnedFolders.length + pinnedFolders.length > 0;

  const FolderRowSkeleton = () => (
    <div
      style={{
        width: '100%',
        padding: '6px 12px',
        boxSizing: 'border-box',
      }}
    >
      <Skeleton
        variant="rounded"
        height={32}
        sx={{
          width: '100%',
          maxWidth: '100%',
        }}
      />
    </div>
  );

  return (
    <Accordion
      expanded={folderListOpen || isLoading}
      onChange={() => dispatch(setFolderListOpen(!folderListOpen))}
      sx={{
        '.MuiAccordionSummary-root.Mui-expanded': {
          height: '48px',
          minHeight: '48px',
        },
        '.MuiAccordionSummary-content': { margin: '0' },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <FilterControlsContainer>
          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <FolderOutlined sx={{ height: '20px', width: '20px' }} />
            <Typography variant="subtitle2" sx={{ fontSize: '15px' }}>
              Folders
            </Typography>
            {(selectedApp !== AppEnum.DEAL_GPT || isDealGPTAdmin) && (
              <IconButton onClick={onNewFolderClick} sx={{ padding: '0px' }}>
                <AddCircleOutline sx={{ color: '#50658E', height: '20px', width: '20px' }} />
              </IconButton>
            )}
          </div>
          <Typography
            variant="caption2"
            sx={{ '&:hover': { cursor: 'pointer' }, fontWeight: '600' }}
            onClick={(e) => {
              e.stopPropagation();
              if (selectedApp === AppEnum.DEAL_GPT) {
                navigate('/intellio-advantage');
                dispatch(setCurrentFolder(undefined));
              } else {
                navigate('/search/folders');
              }
            }}
          >
            {'View All >'}
          </Typography>
        </FilterControlsContainer>
      </AccordionSummary>
      <AccordionDetails
        sx={{
          height: folderListHeight,
          maxHeight: 'fit-content',
          overflowY: 'auto',
          padding: '0px',
        }}
      >
        {isLoading || isSwitchingApps ? (
          <div style={{ overflowY: 'auto', height: '100%', maxHeight: 'fit-content' }}>
            <StyledList>
              {Array.from({ length: 7 }).map((_, idx) => (
                <div key={idx} style={{ width: 'inherit' }}>
                  <FolderRowSkeleton />
                </div>
              ))}
            </StyledList>
          </div>
        ) : !hasFolders ? (
          <Typography variant="body2" sx={{ marginLeft: '20px' }}>
            No folders at the moment.
          </Typography>
        ) : (
          <div style={{ overflowY: 'auto', height: '100%', maxHeight: 'fit-content' }}>
            <StyledList>
              {pinnedFolders.map((folder) => (
                <FolderListItem
                  key={folder.id}
                  folder={folder}
                  onFolderCardClick={() => onFolderCardClick(folder)}
                />
              ))}
              {unpinnedFolders.map((folder) => (
                <FolderListItem
                  key={folder.id}
                  folder={folder}
                  onFolderCardClick={() => onFolderCardClick(folder)}
                />
              ))}
              <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                {isLoading ? (
                  <div style={{ width: '100%', padding: '6px 12px' }}>
                    <Skeleton variant="text" width={120} />
                  </div>
                ) : (
                  shouldShowLoadMoreButton && (
                    <LoadMoreButton onClick={loadMoreFolders}>
                      <Typography variant="caption2">+ Load More</Typography>
                    </LoadMoreButton>
                  )
                )}
              </div>
            </StyledList>
          </div>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default FolderList;
